import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Header from './components/Header';
import FlashcardDeck from './components/FlashcardDeck';
import Statistics from './components/Statistics';
import Settings from './components/Settings';
import SubjectSelector from './components/SubjectSelector';
import { sampleFlashcards, flashcardsBySubject } from './data/flashcards';
import './App.css';

function App() {
  const [currentView, setCurrentView] = useState('subjects');
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [flashcards, setFlashcards] = useState([]);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [stats, setStats] = useState({
    cardsStudied: 0,
    correctAnswers: 0,
    streak: 0,
    bestStreak: 0
  });

  // Load stats from localStorage on app start
  useEffect(() => {
    const savedStats = localStorage.getItem('whatsthis-stats');
    if (savedStats) {
      setStats(JSON.parse(savedStats));
    }
  }, []);

  // Save stats to localStorage whenever stats change
  useEffect(() => {
    localStorage.setItem('whatsthis-stats', JSON.stringify(stats));
  }, [stats]);

  // Update flashcards when subject changes
  useEffect(() => {
    if (selectedSubject === 'all') {
      setFlashcards(sampleFlashcards);
    } else if (selectedSubject && flashcardsBySubject[selectedSubject]) {
      setFlashcards(flashcardsBySubject[selectedSubject]);
    } else {
      setFlashcards([]);
    }
    setCurrentCardIndex(0);
  }, [selectedSubject]);

  const handleCardAnswer = (isCorrect) => {
    setStats(prevStats => {
      const newStreak = isCorrect ? prevStats.streak + 1 : 0;
      return {
        cardsStudied: prevStats.cardsStudied + 1,
        correctAnswers: prevStats.correctAnswers + (isCorrect ? 1 : 0),
        streak: newStreak,
        bestStreak: Math.max(prevStats.bestStreak, newStreak)
      };
    });
  };

  const resetStats = () => {
    setStats({
      cardsStudied: 0,
      correctAnswers: 0,
      streak: 0,
      bestStreak: 0
    });
    setCurrentCardIndex(0);
  };

  const handleSubjectChange = (subject) => {
    setSelectedSubject(subject);
  };

  const handleStartStudying = () => {
    setCurrentView('deck');
  };

  const handleBackToSubjects = () => {
    setCurrentView('subjects');
    setSelectedSubject(null);
  };

  const handleNextQuiz = (nextSubjectKey) => {
    setSelectedSubject(nextSubjectKey);
    setCurrentCardIndex(0);
    // Stay on the deck view to continue with the next quiz
  };

  const pageVariants = {
    initial: { opacity: 0, y: 20 },
    in: { opacity: 1, y: 0 },
    out: { opacity: 0, y: -20 }
  };

  const pageTransition = {
    type: "tween",
    ease: "anticipate",
    duration: 0.4
  };

  const renderCurrentView = () => {
    switch (currentView) {
      case 'subjects':
        return (
          <SubjectSelector
            selectedSubject={selectedSubject}
            onSubjectChange={handleSubjectChange}
            onStartStudying={handleStartStudying}
          />
        );
      case 'deck':
        return (
          <FlashcardDeck
            flashcards={flashcards}
            currentCardIndex={currentCardIndex}
            setCurrentCardIndex={setCurrentCardIndex}
            onCardAnswer={handleCardAnswer}
            stats={stats}
            selectedSubject={selectedSubject}
            onBackToSubjects={handleBackToSubjects}
            onNextQuiz={handleNextQuiz}
          />
        );
      case 'stats':
        return (
          <Statistics
            stats={stats}
            totalCards={flashcards.length}
            onReset={resetStats}
          />
        );
      case 'settings':
        return (
          <Settings
            flashcards={flashcards}
            setFlashcards={setFlashcards}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="App">
      <Header 
        currentView={currentView} 
        setCurrentView={setCurrentView}
        stats={stats}
        selectedSubject={selectedSubject}
        onBackToSubjects={handleBackToSubjects}
      />
      
      <main className="main-content">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentView}
            initial="initial"
            animate="in"
            exit="out"
            variants={pageVariants}
            transition={pageTransition}
            className="view-container"
          >
            {renderCurrentView()}
          </motion.div>
        </AnimatePresence>
      </main>
    </div>
  );
}

export default App; 